import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/api/supabaseClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import logoUrl from '@/assets/logo-horizontal.png';

// Same shell pattern as CarrierAuth / AcceptInvite -- module scope so
// the inputs don't lose focus on every keystroke.
const Shell = ({ title, subtitle, children }) => (
  <div className="min-h-screen bg-prohaul-white text-prohaul-black flex flex-col items-center justify-center px-4 py-10">
    <div className="w-full max-w-sm space-y-6 flex-1 flex flex-col justify-center">
      <div className="text-center">
        <img src={logoUrl} alt="Pro Haul" className="w-72 mx-auto mb-5 block" />
        <h1 className="text-2xl font-stencil font-bold text-prohaul-black tracking-wide">{title}</h1>
        {subtitle && <p className="text-prohaul-black/70 text-sm mt-1">{subtitle}</p>}
      </div>
      {children}
    </div>
    <footer className="text-xs text-prohaul-black/60 flex items-center gap-4 pt-8">
      <a href="/terms" className="hover:text-prohaul-red">Terms</a>
      <span className="opacity-40">·</span>
      <a href="/privacy" className="hover:text-prohaul-red">Privacy</a>
    </footer>
  </div>
);

const ErrorBanner = ({ message }) =>
  message ? (
    <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-start gap-2">
      <AlertCircle className="w-4 h-4 text-prohaul-red flex-shrink-0 mt-0.5" />
      <p className="text-xs text-prohaul-black">{message}</p>
    </div>
  ) : null;

// Reached via the link in a Supabase "reset password" email. Clicking
// that link makes supabase-js establish a temporary recovery session
// automatically (detectSessionInUrl) before this component even
// mounts -- App.jsx routes this path outside the normal auth gate so
// that session doesn't get auto-routed straight into the app before
// the user actually sets a new password.
export default function ResetPassword() {
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    setError(null);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      toast.success('Password updated.');
    } catch (err) {
      // A missing/expired recovery session surfaces here as an auth
      // error (e.g. "Auth session missing") -- the link is one-time
      // use and expires, so this is the expected failure mode for an
      // old or already-used link, not just a generic error.
      setError(err?.message || 'Could not update your password. The reset link may have expired -- request a new one.');
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <Shell title="Password updated" subtitle="You're all set">
        <p className="text-sm text-prohaul-black/70 text-center">Your password has been changed.</p>
        <Button
          onClick={() => navigate('/', { replace: true })}
          className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold"
        >
          Continue
        </Button>
      </Shell>
    );
  }

  return (
    <Shell title="Set a new password">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="password" className="text-prohaul-black">New password</Label>
          <Input
            id="password"
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); if (error) setError(null); }}
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="••••••••"
            className="bg-white border border-prohaul-black/20 text-prohaul-black placeholder:text-prohaul-black/40"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="confirm" className="text-prohaul-black">Confirm password</Label>
          <Input
            id="confirm"
            type="password"
            value={confirm}
            onChange={e => { setConfirm(e.target.value); if (error) setError(null); }}
            required
            minLength={8}
            autoComplete="new-password"
            placeholder="••••••••"
            className="bg-white border border-prohaul-black/20 text-prohaul-black placeholder:text-prohaul-black/40"
          />
        </div>
        <ErrorBanner message={error} />
        <Button type="submit" disabled={busy} className="w-full bg-prohaul-red hover:bg-prohaul-red/90 text-white font-semibold">
          {busy ? 'Updating…' : 'Update password'}
        </Button>
      </form>
    </Shell>
  );
}
